import React, { memo, useCallback, useEffect, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import { createUseStyles } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import HyperDrawer from 'components/HyperDrawer';
import ForceForm from 'components/ForceForm';

import styles from './styles';

const useStyles = createUseStyles(styles, 'FormSmartFieldConfig');

const CRUD_TYPE_BOTH = 0;
const CRUD_TYPE_CREATION = 1;
const CRUD_TYPE_EDIT = 2;

const initialState = {
    data: {},
    originalData: {},
    isOpen: false,
    isSaving: false,
    errors: {},
    fieldType: null,
    fieldConfig: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                data: action.data || {},
                originalData: action.data || {},
                isOpen: true,
                fieldType: action.fieldType,
                fieldConfig: action.fieldConfig,
                errors: {},
            };
        case 'close':
            return { ...state, isOpen: false, isSaving: false };
        case 'changeData':
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data,
                },
            };
        case 'setErrors':
            return { ...state, isSaving: false, errors: action.errors };
        case 'setSaving':
            return { ...state, isSaving: action.value };
        default:
            throw new Error('No action provided');
    }
}

const FormSmartFieldConfig = ({ onRef, onSave: onSaveCallback, stackIndex = 1, onStack }) => {
    const { t } = useWeb5Context();
    const [state, dispatch] = useReducer(reducer, initialState);

    const classes = useStyles();

    // Build schema based on field type
    const schema = useMemo(() => {
        if (!state.isOpen) return null;

        const hasSeparateConfig = state.data.hasSeparateEditConfig;
        const fieldType = state.fieldType;
        const fieldConfig = state.fieldConfig;

        const baseCheckboxFields = [
            {
                schemaId: 'visible',
                label: 'cfm_label_visible',
                uiType: 'checkbox',
            },
            {
                schemaId: 'mandatory',
                label: 'cfm_label_mandatory',
                uiType: 'checkbox',
            },
            {
                schemaId: 'readOnly',
                label: 'cfm_label_readonly',
                uiType: 'checkbox',
            },
        ];

        // Configure default value field based on field type
        const getDefaultValueField = (schemaIdPrefix = '') => {
            const schemaId = schemaIdPrefix ? `${schemaIdPrefix}_defaultValue` : 'defaultValue';
            let defaultValueField = {
                schemaId,
                label: 'label_sf_default_value',
                helpTip: 'label_sf_default_value_disclaimer',
                uiType: 'text',
                placeholder: 'cfm_placeholder_type_value',
            };

            // Modify based on field type
            switch (fieldType) {
                case 'bit':
                    defaultValueField.uiType = 'checkbox';
                    delete defaultValueField.placeholder;
                    break;
                case 'list':
                case 'multivalue':
                    defaultValueField.uiType = 'list';
                    defaultValueField.placeholder = 'label_selectone';
                    defaultValueField.key = fieldConfig?.valueListRelated?.value;
                    defaultValueField.valueObjectNeeded = fieldConfig?.valueListRelated?.value;
                    defaultValueField.isValueObject = false;
                    break;
                case 'int':
                case 'bigint':
                case 'decimal':
                    defaultValueField.uiType = 'number';
                    defaultValueField.placeholder = 'cfm_placeholder_integer';
                    break;
                case 'date':
                    defaultValueField.uiType = 'date';
                    break;
                case 'datetime':
                    defaultValueField.uiType = 'datetime';
                    break;
                default:
                    // text, textarea, etc. keep as 'text'
                    break;
            }

            return defaultValueField;
        };

        const baseDefaultValueField = getDefaultValueField();

        const composition = [];

        if (!hasSeparateConfig) {
            const generalSettingsComposition = [
                {
                    schemaId: 'generalSettings',
                    uiType: 'checkboxGroup',
                    label: 'label_sf_config_behaviour',
                    composition: baseCheckboxFields,
                },
                baseDefaultValueField,
            ];

            composition.push(
                {
                    schemaId: 'generalConfigSection',
                    uiType: 'section',
                    label: 'label_sf_config_general',
                    composition: generalSettingsComposition,
                },
                {
                    schemaId: 'hasSeparateEditConfig',
                    label: 'label_sf_config_edition_question',
                    uiType: 'switch',
                    helpTip: 'label_sf_config_edition_question_disclaimer',
                },
            );
        } else {
            // Dual config mode - Creation
            const creationSettingsComposition = [
                {
                    schemaId: 'creation_generalSettings',
                    uiType: 'checkboxGroup',
                    label: 'label_sf_config_general',
                    composition: baseCheckboxFields.map((field) => ({
                        ...field,
                        schemaId: `creation_${field.schemaId}`,
                    })),
                },
                getDefaultValueField('creation'),
            ];

            // Dual config mode - Edit
            const editSettingsComposition = [
                {
                    schemaId: 'edit_generalSettings',
                    uiType: 'checkboxGroup',
                    label: 'label_sf_config_general',
                    composition: baseCheckboxFields.map((field) => ({
                        ...field,
                        schemaId: `edit_${field.schemaId}`,
                    })),
                },
                getDefaultValueField('edit'),
            ];

            composition.push(
                {
                    schemaId: 'creationConfigSection',
                    uiType: 'section',
                    label: 'label_sf_config_create',
                    composition: creationSettingsComposition,
                },
                {
                    schemaId: 'editConfigSection',
                    uiType: 'section',
                    label: 'label_sf_config_edition',
                    composition: editSettingsComposition,
                },
                {
                    schemaId: 'hasSeparateEditConfig',
                    label: 'label_sf_config_edition_question',
                    uiType: 'switch',
                    helpTip: 'label_sf_config_edition_question_disclaimer',
                },
            );
        }

        // Build context for value list fields
        const context = {};
        if (fieldType === 'list' || fieldType === 'multivalue') {
            const table = fieldConfig?.valueListRelated?.value;
            context.fields = {
                defaultValue: { table },
                creation_defaultValue: { table },
                edit_defaultValue: { table },
            };
        }

        return {
            schemaId: 'smartFieldConfig',
            layout: {
                schemaId: 'smartFieldConfig',
                composition,
            },
            context: Object.keys(context).length > 0 ? context : undefined,
            required: [],
        };
    }, [state.isOpen, state.data.hasSeparateEditConfig, state.fieldType, state.fieldConfig]);

    useEffect(() => {
        onRef &&
            onRef({
                open({ valueId, fieldType, fieldConfig, existingConfigs }) {
                    const parseDefaultValue = (value) => {
                        if (value === null || value === undefined || value === '') return null;

                        switch (fieldType) {
                            case 'bit':
                                return value === '1' || value === true;
                            case 'list':
                            case 'multivalue':
                                return value ? Number(value) : null;
                            case 'int':
                            case 'bigint':
                            case 'decimal':
                                return value ? Number(value) : null;
                            default:
                                return value;
                        }
                    };

                    const configsForValue = (existingConfigs || []).filter(
                        (c) => c.value === valueId,
                    );

                    let data = {
                        value: valueId,
                        hasSeparateEditConfig: false,
                        visible: true,
                        mandatory: false,
                        readOnly: false,
                        defaultValue: null,

                        creation_visible: true,
                        edit_visible: true,
                    };

                    if (configsForValue.length === 0) {
                        data.hasSeparateEditConfig = false;
                    } else if (configsForValue.length === 1) {
                        const config = configsForValue[0];
                        if (config.crudType === CRUD_TYPE_BOTH) {
                            data = {
                                ...data,
                                hasSeparateEditConfig: false,
                                visible: config.visible,
                                mandatory: config.mandatory,
                                readOnly: config.readOnly,
                                defaultValue: parseDefaultValue(config.defaultValue),
                            };
                        }
                    } else if (configsForValue.length === 2) {
                        const creationConfig = configsForValue.find(
                            (c) => c.crudType === CRUD_TYPE_CREATION,
                        );
                        const editConfig = configsForValue.find(
                            (c) => c.crudType === CRUD_TYPE_EDIT,
                        );

                        data = {
                            ...data,
                            hasSeparateEditConfig: true,
                            creation_visible: creationConfig?.visible ?? true,
                            creation_mandatory: creationConfig?.mandatory ?? false,
                            creation_readOnly: creationConfig?.readOnly ?? false,
                            creation_defaultValue: parseDefaultValue(creationConfig?.defaultValue),
                            edit_visible: editConfig?.visible ?? true,
                            edit_mandatory: editConfig?.mandatory ?? false,
                            edit_readOnly: editConfig?.readOnly ?? false,
                            edit_defaultValue: parseDefaultValue(editConfig?.defaultValue),
                        };
                    }

                    dispatch({
                        type: 'init',
                        data,
                        fieldType,
                        fieldConfig,
                    });
                },
            });
    }, [onRef]);

    const onChange = useCallback(
        (data) => {
            // Detect switch from general to separate config
            if (data.hasSeparateEditConfig && !state.data.hasSeparateEditConfig) {
                // Copy general config to creation (insert) config
                const migratedData = {
                    ...data,
                    creation_visible: state.data.visible ?? true,
                    creation_mandatory: state.data.mandatory ?? false,
                    creation_readOnly: state.data.readOnly ?? false,
                    creation_defaultValue: state.data.defaultValue ?? null,
                };
                dispatch({ type: 'changeData', data: migratedData });
            } else if (!data.hasSeparateEditConfig && state.data.hasSeparateEditConfig) {
                // Copy creation config to general config when switching back
                const migratedData = {
                    ...data,
                    visible: state.data.creation_visible ?? true,
                    mandatory: state.data.creation_mandatory ?? false,
                    readOnly: state.data.creation_readOnly ?? false,
                    defaultValue: state.data.creation_defaultValue ?? null,
                };
                dispatch({ type: 'changeData', data: migratedData });
            } else {
                dispatch({ type: 'changeData', data });
            }
        },
        [state.data],
    );

    const onCancel = useCallback(() => {
        onStack(1);
        dispatch({ type: 'close' });
    }, [onStack]);

    const onSave = useCallback(() => {
        dispatch({ type: 'setSaving', value: true });

        // Helper to format default value based on field type
        const formatDefaultValue = (value) => {
            if (value === null || value === undefined || value === '') return '';

            switch (state.fieldType) {
                case 'bit':
                    return value ? '1' : '0';
                case 'list':
                case 'multivalue':
                    return String(value?.value || value);
                default:
                    return String(value);
            }
        };

        const configs = [];

        if (!state.data.hasSeparateEditConfig) {
            // Single config
            configs.push({
                value: state.data.value,
                visible: state.data.visible ?? true,
                mandatory: state.data.mandatory ?? false,
                readOnly: state.data.readOnly ?? false,
                defaultValue: formatDefaultValue(state.data.defaultValue),
                crudType: CRUD_TYPE_BOTH,
            });
        } else {
            // Dual config
            configs.push({
                value: state.data.value,
                visible: state.data.creation_visible ?? true,
                mandatory: state.data.creation_mandatory ?? false,
                readOnly: state.data.creation_readOnly ?? false,
                defaultValue: formatDefaultValue(state.data.creation_defaultValue),
                crudType: CRUD_TYPE_CREATION,
            });
            configs.push({
                value: state.data.value,
                visible: state.data.edit_visible ?? true,
                mandatory: state.data.edit_mandatory ?? false,
                readOnly: state.data.edit_readOnly ?? false,
                defaultValue: formatDefaultValue(state.data.edit_defaultValue),
                crudType: CRUD_TYPE_EDIT,
            });
        }

        onSaveCallback && onSaveCallback(state.data.value, configs);
        onStack(1);
        dispatch({ type: 'close' });
    }, [state.data, state.fieldType, onSaveCallback, onStack]);

    const locales = useMemo(
        () => ({
            title: t('label_sf_config_dynamism'),
            cancel: t('cfm_action_cancel'),
            save: t('cfm_action_save'),
        }),
        [t],
    );

    return (
        <HyperDrawer
            isOpen={state.isOpen}
            locales={locales}
            isSaving={state.isSaving}
            onCancel={onCancel}
            onSave={onSave}
            stackIndex={stackIndex}
            data={state.data}
            originalData={state.originalData}
            className="fields__smart-field-config-form"
        >
            {schema && (
                <ForceForm
                    values={state.data}
                    errors={state.errors}
                    onChange={onChange}
                    schema={schema}
                    mode="create"
                    className={classes.root}
                    stackIndex={stackIndex}
                />
            )}
        </HyperDrawer>
    );
};

FormSmartFieldConfig.propTypes = {
    onRef: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    stackIndex: PropTypes.number,
    onStack: PropTypes.func,
};

export default memo(FormSmartFieldConfig);
